import { useEffect, useState } from 'react';

import { riderApi } from '@/api/rider.api';
import { isOnShift, type RiderProfile } from '@/api/types';
import { LOCATION_PING_INTERVAL_MS } from '@/lib/env';

/**
 * Whether dispatch can actually see this phone.
 *
 * `off` is not a failure — it is what being off shift means.
 */
export type PingStatus = 'off' | 'reporting' | 'blocked' | 'unsupported';

export type LocationPing = {
  status: PingStatus;
  /**
   * When the last ping actually landed. Surfaced on the account screen,
   * because "dispatch can see me" is otherwise invisible and a rider would
   * have to take it on trust for a whole shift.
   */
  at: Date | null;
  message: string | null;
  /** Where the rider was when the last ping landed, for ordering the drops. */
  position: { latitude: number; longitude: number } | null;
};

/**
 * What came back from the outside world. Everything else about the ping — off
 * shift, unsupported — is DERIVED from props rather than stored, so there is
 * no state to get out of step with the profile it describes.
 *
 * `session` is what makes a result belong to one stretch of one rider being on
 * shift. Without it, going off shift and back on would briefly show the last
 * ping of the previous shift as if it were current.
 */
type PingResult = {
  session: string;
  at: Date | null;
  blocked: string | null;
  /**
   * The last position that actually landed.
   *
   * Kept because the run sheet orders the drops by distance from here. It is
   * the SAME fix already being sent to dispatch — asking the browser a second
   * time for the same thing would double the GPS wake-ups for nothing.
   */
  at_point: { latitude: number; longitude: number } | null;
};

/**
 * Posts the browser's position while the rider is on shift, and not otherwise.
 *
 * Tied to the SHIFT rather than to carrying a job, because the position is
 * what lets dispatch hand the next delivery to whoever is nearest — a question
 * asked about riders holding nothing at all. Off shift it stops completely: an
 * app that kept reporting a rider's location after they finished for the day
 * would be tracking a person rather than a delivery.
 *
 * FOREGROUND ONLY, and this is a real limitation rather than a rough edge. A
 * web page gets no background-location entitlement on either platform, so the
 * stream pauses when the tab is hidden — which, for a rider who switches to
 * Maps for the whole leg, is most of it. `visibilitychange` fires a ping the
 * moment they come back, so the gap is a gap rather than a silence. It is why
 * nothing in this UI is sold as live tracking, and why the account screen says
 * plainly what it does and does not do.
 */
export function useLocationPing(rider: RiderProfile | null | undefined): LocationPing {
  const riderId = rider?.id ?? null;
  const onShift = isOnShift(rider);
  const session = `${riderId ?? 'none'}:${onShift}`;

  const [result, setResult] = useState<PingResult>({
    session: '',
    at: null,
    blocked: null,
    at_point: null,
  });

  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  useEffect(() => {
    if (!riderId || !onShift || !supported) return;

    let cancelled = false;

    /*
     * Scoped to THIS effect run, not held in a ref across runs.
     *
     * As a ref it survived the unmount/remount React does in StrictMode: the
     * first run set it true and awaited a fix, the cleanup cancelled that run
     * without clearing the flag, and the second run's immediate ping saw a
     * send still "in flight" and skipped. The rider then appeared on the
     * dispatch board twenty seconds late — once per remount, and invisibly,
     * because a skipped ping looks exactly like a ping that has not come round
     * yet.
     *
     * What the flag is actually for is not overlapping THIS interval's sends,
     * and that is a fact about one effect run.
     */
    let inFlight = false;

    const send = async () => {
      if (inFlight || cancelled) return;
      inFlight = true;

      try {
        const position = await currentPosition();
        if (cancelled) return;

        await riderApi.pushLocation(riderId, {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyM: Number.isFinite(position.coords.accuracy)
            ? position.coords.accuracy
            : undefined,
        });
        if (cancelled) return;

        setResult({
          session,
          at: new Date(),
          blocked: null,
          at_point: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
        });
      } catch (error) {
        if (cancelled) return;

        // The BROWSER refusing is worth telling the rider about: dispatch is
        // routing work by a position it is no longer getting, and the fix is
        // in a settings screen only they can reach.
        if (isPositionError(error)) {
          setResult((previous) => ({
            session,
            at: previous.session === session ? previous.at : null,
            at_point: previous.session === session ? previous.at_point : null,
            blocked: positionErrorMessage(error),
          }));
          return;
        }

        /*
         * A ping that did not reach the server.
         *
         * Deliberately silent and deliberately not retried: this fires every
         * twenty seconds all shift, and the next one is a better answer than a
         * resend of a position the rider has already left. Surfacing it would
         * put a network error on screen every time they went into a lift.
         */
      } finally {
        inFlight = false;
      }
    };

    // First one immediately, so a rider who has just gone on shift appears on
    // the board now rather than at the top of the next interval.
    void send();
    const timer = window.setInterval(() => void send(), LOCATION_PING_INTERVAL_MS);

    // Coming back from Maps should not mean waiting out the rest of a tick.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void send();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [riderId, onShift, supported, session]);

  if (!riderId || !onShift) return { status: 'off', at: null, message: null, position: null };

  if (!supported) {
    return {
      status: 'unsupported',
      at: null,
      message: 'This browser cannot share your location. The office will send you jobs without it.',
      position: null,
    };
  }

  // Anything recorded under a previous shift is not news about this one.
  const live = result.session === session ? result : null;

  if (live?.blocked) {
    return { status: 'blocked', at: live.at, message: live.blocked, position: live.at_point };
  }
  return {
    status: 'reporting',
    at: live?.at ?? null,
    message: null,
    position: live?.at_point ?? null,
  };
}

/**
 * Asks the browser once, on purpose.
 *
 * The permission prompt belongs at the moment the rider goes on shift — while
 * they are looking at the phone deciding to work — rather than twenty seconds
 * later on a bike. Called by the shift switch for exactly that reason.
 */
export function primeLocationPermission(): Promise<void> {
  if (!('geolocation' in navigator)) return Promise.resolve();
  return currentPosition().then(
    () => undefined,
    () => undefined,
  );
}

function currentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      // Shorter than the ping interval, so a fix that is not coming fails
      // before the next attempt rather than piling up behind it.
      timeout: 15_000,
      // A fix from the last half minute is good enough for a dispatch board
      // and saves waking the GPS on every tick.
      maximumAge: 30_000,
    });
  });
}

/**
 * `GeolocationPositionError` is not a real class on every engine, so
 * `instanceof` is not safe to lean on. Shape is.
 */
function isPositionError(error: unknown): error is GeolocationPositionError {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
}

function positionErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case 1: // PERMISSION_DENIED
      return 'Location is blocked for this site. Allow it in your browser settings so the office can send you the nearest jobs.';
    case 2: // POSITION_UNAVAILABLE
      return 'Your phone cannot get a fix right now. It will keep trying.';
    case 3: // TIMEOUT
      return 'Your phone is taking too long to find a fix. It will keep trying.';
    default:
      return 'Your location is not reaching the office.';
  }
}
